import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { TournamentDetailsService } from '../tournament-details.service';
import { renderStructure, renderContainer, compositions } from '../../../../assets/courthive-components/courthive-components.es';
import { TournamentHeaderComponent } from '../tournament-header/tournament-header.component';
import { IonicModule, NavController } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule, JsonPipe } from '@angular/common';
import { END_POINT } from '../../../../environments/environment';
import { Socket } from 'ngx-socket-io';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
@Component({
  selector: 'app-tournament-draws',
  templateUrl: './tournament-draws.component.html',
  styleUrls: ['./tournament-draws.component.scss'],
  imports: [
    TournamentHeaderComponent,
    IonicModule,
    FormsModule,
    CommonModule
  ],
  standalone: true
})
export class TournamentDrawsComponent implements OnInit {
  private tournamentDetailsService = inject(TournamentDetailsService);
  private socket = inject(Socket);
  @ViewChild('drawContainer', { static: true }) drawContainer!: ElementRef<HTMLDivElement>;

  tournamentId!: string;
  tournamentUid!: string;
  partner!: string; // to check if it's CourtHive
  eventId: string = '';
  events: any[] = [];
  draws: string[] = [];
  drawType: string = '';
  drawsResp: any[] = [];
  drawList: any[] = [];            // List of available draws (from drawsData)
  selectedDrawId = '';
  matchUps: any[] = [];

  // CourtHive composition configuration
  composition = {
    ...compositions['US Open'],
    configuration: {
      ...compositions['Australian'].configuration,
      flags: true,
      drawPositions: true,
      scheduleInfo: true,
      roundHeader: true
    }
  };

  eventHandlers = {};
  private readonly destroy$: Subject<void> = new Subject();
  public skeletonText: boolean = true;

  constructor(
    private activatedRoute: ActivatedRoute, 
    private navCtrl: NavController) { }

  async ngOnInit() {
    this.tournamentId = this.activatedRoute?.snapshot?.parent?.params.id;
    this.loadTournament(this.tournamentId);
    this.socket.emit('join-room', `tournament/${this.tournamentId}/score`);
    this.socketSubscriptions();
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.socket.emit('leave-room', `tournament/${this.tournamentId}/score`);
  }

  private socketSubscriptions() {
    this.socket.fromEvent(`tournament/score`).pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      if (data.score) {
        const updatedScore = data;
        const matchIndex = this.matchUps.findIndex(
          (match: any) => match._id === updatedScore._id
        );
  
        if (matchIndex === -1) {
          return;
        }
  
        const updatedMatch = this.matchUps[matchIndex];
        updatedMatch.score = updatedScore.score;
        updatedMatch.status = updatedScore.matchStatus;
        updatedMatch.winningSide = updatedScore.winningSide;  
        updatedMatch.matchUpStatus = updatedScore.matchStatus;      
        window.dispatchEvent(new CustomEvent('matchUpScoreUpdate', {
          detail: {
            matchId: updatedMatch.matchUpId,
            score: updatedScore.score,
            matchUpStatus: updatedScore.matchStatus,
            winningSide: updatedScore.winningSide,
            status: updatedScore.matchStatus,
          }
        }));
      }
    });
  }

  /** Load tournament details and fetch first event’s draws */
  async loadTournament(tournamentId: string) {
    this.skeletonText = true;
    this.tournamentDetailsService.getTournament(tournamentId, false).subscribe({
      next: (res) => {
        this.partner = res?.partner;
        this.tournamentUid = res?.tournamentId;

        // ✅ CourtHive-only logic
        if (this.partner?.toLowerCase() !== 'tmx') {
          console.log('Non-CourtHive tournament — skipping draws logic.');
          return;
        }

        // ✅ Store event data
        this.events = Array.isArray(res) ? res : res?.events || [];
        this.eventId = this.events?.[0]?.eventId || '';

        if (this.eventId) {
          this.getDrawMatches(this.eventId, this.tournamentUid);
        }        
      },
      error: (err) => console.error(err)
    });
  }

  /** Event dropdown change */
  onEventChange(event: any) {
    const eventId = event.detail.value;
    this.getDrawMatches(eventId, this.tournamentUid);
  }

  /** Draw type dropdown change */
  onDrawEventChange(event: any) {
    this.drawType = event.detail.value;
    const selectedDraw = this.drawsResp.find((d) => d.drawId === this.selectedDrawId);
    this.renderDrawByType(this.drawType, selectedDraw);
  }

  /** Fetch and process draws dynamically */
  getDrawMatches(eventId: string, tournamentId: string) {
    this.tournamentDetailsService.getDrawMatches(tournamentId, eventId).subscribe((res) => {
      this.drawsResp = Array.isArray(res?.data?.eventData?.drawsData)
        ? res.data.eventData.drawsData
        : Object.values(res?.data?.eventData?.drawsData || {});

      this.drawList = this.drawsResp.map((d: any) => ({
        drawId: d.drawId,
        drawName: d.drawName || d.structures?.[0]?.structureName || 'Untitled Draw',
        structures: d.structures
      }));

      this.selectedDrawId = this.drawList?.[0]?.drawId || '';

      if (!this.drawsResp?.length) {
        console.warn('No draws found for event:', eventId);
        return;
      }

      // ✅ Build draws list dynamically from structureName
      this.draws = (this.drawsResp || [])
        .flatMap((d: any) =>
          (d.structures || []).map((s: any) => s.structureName?.trim()).filter(Boolean)
        )
        // Remove duplicates
        .filter((v: string, i: number, arr: string[]) => arr.indexOf(v) === i)
        // ✅ Reverse order (per acceptance criteria)
        .reverse();


      // ✅ Default draw type (first after reverse)
      this.drawType = this.draws?.[0] || '';

      // ✅ Render first available structure
      this.renderDrawByType(this.drawType);
    });
  }


  onDrawChange(event: any) {
    this.selectedDrawId = event.detail.value;
    this.updateDrawStructures(this.selectedDrawId);
  }

  updateDrawStructures(drawId: string) {
    const selectedDraw = this.drawsResp.find((d) => d.drawId === drawId);
    if (!selectedDraw) return;

    this.draws = (selectedDraw.structures || [])
      .map((s: any) => s.structureName?.trim())
      .filter(Boolean)
      .reverse();

    this.drawType = this.draws?.[0] || '';
    this.renderDrawByType(this.drawType, selectedDraw);
  }

  /** Render selected draw type dynamically */
  async renderDrawByType(drawType: string, drawOverride?: any, isScoreUpdated?: boolean, updatedScore?: any) {
    const draw = drawOverride || this.drawsResp.find((d) =>
      (d.structures || []).some(
        (s: any) => s.structureName?.toLowerCase() === drawType?.toLowerCase()
      )
    );

    if (!draw) return;

    let matchUps:any = [];
    const structure = (draw.structures || []).find(
      (s: any) => s.structureName?.toLowerCase() === drawType?.toLowerCase()
    ) || draw.structures?.[0];

    // if the render is
    if (isScoreUpdated) {
      matchUps = this.matchUps;
    } else {
      matchUps = Object.values(structure?.roundMatchUps || {}).flat();
      this.matchUps = matchUps;
      this.drawType = drawType;
    }
    if (!Array.isArray(matchUps) || !matchUps.length) {
      return
    };

    matchUps.forEach((m: any) => (m.stage = drawType.toUpperCase()));

    const context = {
      structureId: structure?.structureId,
      drawId: draw?.drawId,
      eventType: this.events?.[0]?.eventType,
      drawType
    };    
    // Render structure
    const content = renderStructure({
      initialRoundNumber: 1,
      selectedMatchUpId: null,
      searchActive: false,
      composition: this.composition,
      structureId: context.structureId,
      finalColumn: undefined,
      matchUps,
      minWidth: undefined,
      context,
      eventHandlers: {
        matchUpClick: ({ pointerEvent, matchUp }: { pointerEvent: PointerEvent; matchUp: any }) => {
          if(matchUp?._id){
            this.openMatchUpDetails(matchUp);
          }
        }
      }
    });

    // Render inside container
    const containerEl = this.drawContainer.nativeElement;
    containerEl.innerHTML = '';
    containerEl.appendChild(renderContainer({ theme: this.composition.theme, content }));
    this.skeletonText = false;
  }

  openMatchUpDetails(matchUp: any) {
    this.navCtrl.navigateRoot(`live-scoring/${matchUp._id}?tab=draw`);
  }

}
